import { useState } from 'react';
import { UserRoundIcon } from 'lucide-react';
import { useAdmin } from '../AdminContext';
import { btnGhost, btnPrimary, inputCls } from '../ui/styles';
import { StaffDot } from './StaffDot';
import { performerChoice, staffList, staffName } from '../../core/rules/salonDays';
import type { AgendaAppointment } from '../../core/types/agenda';
import type { AgendaControl } from './useAgenda';
type Props = {
  appointment: AgendaAppointment;
  agenda: AgendaControl;
  /** Alleen de eigenaar wijzigt de uitvoerder. */
  canChange: boolean;
};

/**
 * "Behandeld door …" in het afspraakdetail, met wijzigen. De keuze wordt gecontroleerd: werkt zij
 * die dag, en doet zij deze behandeling? Wie niet past staat er wel, maar is niet te kiezen, met de
 * reden erbij. De server controleert hetzelfde (422).
 */
export function PerformerRow({
  appointment: a,
  agenda,
  canChange
}: Props) {
  const {
    site
  } = useAdmin();
  const team = staffList(site.staff);
  const [editing, setEditing] = useState(false);
  const [pick, setPick] = useState(a.staffId);
  const busy = agenda.isBusy(a.id);
  const options = team.filter(s => s.active || s.id === a.staffId).map(s => ({
    s,
    problem: performerChoice({
      ...a,
      staffId: s.id
    }, site.openingHours, site.salonExceptions, team)
  }));
  const chosen = options.find(o => o.s.id === pick);
  const save = async () => {
    const moved = await agenda.move(a.id, {
      date: a.date,
      time: a.time,
      staffId: pick
    });
    if (moved) setEditing(false);
  };
  if (team.length < 2) return null;
  return <div className="flex items-start gap-2.5" data-id="element-482" data-mp="3hiiuw2g2idwl">
      <dt className="sr-only" data-id="element-483" data-mp="3mh3tjxfo8pix">Behandeld door</dt>
      <UserRoundIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-484" data-mp="3mgc28yt3xdyg" />
      <dd className="min-w-0 flex-1" data-id="element-485" data-mp="3r9dl1hwfpxnb">
        {!editing ? <span className="flex flex-wrap items-center gap-2 text-ink" data-id="element-486" data-mp="2tt833agp3tul">
            <StaffDot staff={team} id={a.staffId} data-id="element-487" data-mp="2yrjbdgrcdwap" />
            Behandeld door {staffName(team, a.staffId)}
            {canChange && <button type="button" onClick={() => (setPick(a.staffId), setEditing(true))} className={`${btnGhost} -my-1`} data-id="element-488" data-mp="2qps6eb07h3jw">
                Wijzigen
              </button>}
          </span> : <div className="space-y-2" data-id="element-489" data-mp="30g3l8slfgx5k">
            <label className="block text-[13px] text-muted" htmlFor={`uitvoerder-${a.id}`} data-id="element-490" data-mp="2vhjyipz0fhc5">
              Wie behandelt?
            </label>
            <select id={`uitvoerder-${a.id}`} value={pick} onChange={e => setPick(e.target.value)} className={inputCls} data-id="element-491" data-mp="27t0q8afyn4k2">
              {options.map(({
            s,
            problem
          }) => <option key={s.id} value={s.id} disabled={problem !== null && s.id !== a.staffId} data-id="element-492" data-mp="2hpvkgh57aisj">
                  {s.name}
                  {problem ? ` (${problem})` : ''}
                </option>)}
            </select>
            {chosen?.problem && <p className="text-[12px] text-clay" data-id="element-493" data-mp="3t3hdide6l4jc">{chosen.problem}.</p>}
            <div className="flex flex-wrap gap-2" data-id="element-494" data-mp="3o5fs3lta8t4p">
              <button type="button" onClick={() => void save()} disabled={busy || pick === a.staffId || Boolean(chosen?.problem)} aria-busy={busy} className={btnPrimary} data-id="element-495" data-mp="3vpqdblqmpicj">
                {busy ? 'Opslaan…' : `${staffName(team, pick)} behandelt`}
              </button>
              <button type="button" onClick={() => setEditing(false)} className={btnGhost} data-id="element-496" data-mp="3mqv7wbvescaa">
                Laat staan
              </button>
            </div>
          </div>}
      </dd>
    </div>;
}